import { resolveMeterColor, useTranslation } from "@next-platform/client-common-core";
import type { MeterColorTheme, MeterDefinition, PublicMeterView } from "@next-platform/packages-ts-types";
import { MeterType } from "@next-platform/packages-ts-types";
import { useMemo } from "react";

import type { RenderTokenChip } from "@/components/Rulebook/tokenChipTint";
import { useMeterColorTheme } from "@/hooks";
import createRulebookTokenRenderer from "@/pages/RulebookEditor/utils/createRulebookTokenRenderer";
import { SUFFIX_LABEL } from "@/pages/RulebookEditor/utils/rulebookTokens";

type UseProfileTokenRendererParams = {
  definitionMeters?: MeterDefinition[];
  publicMeters?: PublicMeterView[];
};

type ProfileTokenNames = { user: string; char: string };

const TOKEN_BRACE_LENGTH = 2;

const getProfileMeterDefault = (meter: MeterDefinition): number => {
  if (meter.type === MeterType.CLOCK || meter.type === MeterType.DICE) {
    return 0;
  }
  return meter.default;
};

const createPublicProfileTokenRenderer = ({
  publicMeters,
  names,
  theme,
}: {
  publicMeters?: PublicMeterView[];
  names: ProfileTokenNames;
  theme: MeterColorTheme;
}): RenderTokenChip => {
  const metersById = new Map((publicMeters ?? []).map((meter) => [meter.id, meter] as const));
  return (raw) => {
    const token = raw.slice(TOKEN_BRACE_LENGTH, raw.length - TOKEN_BRACE_LENGTH).trim();
    if (token === "user") {
      return { tone: "name", label: names.user };
    }
    if (token === "char") {
      return { tone: "name", label: names.char };
    }
    const [namespace, meterId, suffix, ...extraParts] = token.split(".");
    const meter = namespace === "meters" && meterId ? metersById.get(meterId) : undefined;
    if (!meter || extraParts.length > 0) {
      return { tone: "meter", label: raw };
    }

    let label: string | undefined;
    if (suffix === undefined) {
      label = meter.label;
    } else if (suffix === "level" && meter.type !== MeterType.CLOCK) {
      label = `${meter.label} ${SUFFIX_LABEL.level}`;
    } else if (suffix === "clock" && meter.type === MeterType.CLOCK) {
      label = `${meter.label} ${SUFFIX_LABEL.clock}`;
    } else if (suffix === "caption") {
      label = `${meter.label} ${SUFFIX_LABEL.caption}`;
    }

    if (label === undefined) {
      return { tone: "meter", label: raw };
    }
    const color = resolveMeterColor({ color: meter.color, theme });
    return { tone: "meter", label, ...(color ? { color } : {}) };
  };
};

const useProfileTokenRenderer = ({ definitionMeters, publicMeters }: UseProfileTokenRendererParams): RenderTokenChip => {
  const { t } = useTranslation();
  const theme = useMeterColorTheme();
  return useMemo(() => {
    const names: ProfileTokenNames = { user: t("rulebookEditor:tokens.user", "User"), char: t("rulebookEditor:tokens.char", "Bot") };
    if (definitionMeters) {
      return createRulebookTokenRenderer({
        meters: definitionMeters,
        values: Object.fromEntries(definitionMeters.map((meter) => [meter.id, getProfileMeterDefault(meter)])),
        names,
        emptyNowLabel: t("rulebookEditor:tokens.emptyNow", "(empty at this value)"),
        theme,
      });
    }
    return createPublicProfileTokenRenderer({ publicMeters, names, theme });
  }, [definitionMeters, publicMeters, t, theme]);
};

export default useProfileTokenRenderer;
